/**
 * useAppAccent — resolve + stamp the per-app Aurora accent on `<html>`.
 *
 * Per §2–§3 of `K-OS Vault/Patterns/k-os-aurora-design-system.md`:
 *   resolve(app) = userPref[app] ?? PLATFORM_DEFAULT   // '#5B5BD6'
 * then derive the four shades for the active theme (`deriveAccent`) and set
 * `--accent / --accent-strong / --accent-soft / --accent-ink` inline on
 * `document.documentElement`. Every descendant reads `var(--accent*)` — no
 * prop-drilling, no per-component logic.
 *
 * Theme reactivity: the derivation differs for light vs dark, so the effect
 * observes `data-theme` mutations on `<html>` and recomputes when it flips
 * (the theme itself is owned by `useTweaks`).
 *
 * Charts reactivity: `apps/web` finance charts re-read tokens via a
 * MutationObserver watching *attributes* (`data-accent` et al.), which cannot
 * see inline-style changes. So we also stamp `data-accent="<resolved hex>"`
 * whenever the accent changes — that attribute write trips their observer and
 * they re-read the (now-updated) `var(--accent*)` values.
 */

import { useEffect } from 'react';
import { deriveAccent, onAccentText, type AccentTheme } from '../lib/accent';

/** Platform fallback accent (Indigo) — used for any app without a pref. */
export const PLATFORM_DEFAULT_ACCENT = '#5B5BD6';

/**
 * Fallback per-app accent map: empty ⇒ every app resolves to Indigo. Callers
 * that have the per-user server prefs (A4) pass their resolved map as the
 * second arg (`apps/web` wires `useAccentPrefs()` from
 * `apps/web/src/api/preferences.ts` in `__root.tsx`).
 */
export const DEFAULT_ACCENT_PREFS: Readonly<Record<string, string>> = {};

function currentTheme(): AccentTheme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * Resolve the accent for `app`, derive shades for the active theme, and stamp
 * them (plus the `data-accent` chart marker) on `<html>`. Recomputes on app /
 * pref change and on theme flips.
 *
 * @param app    current app slug (e.g. `'finance'`, `'health'`, `'tasks'`).
 * @param prefs  per-app accent overrides; defaults to the temporary A2 map.
 */
export function useAppAccent(
  app: string,
  prefs: Readonly<Record<string, string>> = DEFAULT_ACCENT_PREFS,
): void {
  const base = prefs[app] ?? PLATFORM_DEFAULT_ACCENT;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    const apply = () => {
      const shades = deriveAccent(base, currentTheme());
      root.style.setProperty('--accent', shades.a);
      root.style.setProperty('--accent-strong', shades.strong);
      root.style.setProperty('--accent-soft', shades.soft);
      root.style.setProperty('--accent-ink', shades.ink);
      // Text colour that sits ON a solid --accent fill (primary buttons,
      // filled controls). Flips white↔dark by contrast so light accents
      // (amber, etc.) stay legible. Theme-independent (keys off the base).
      root.style.setProperty('--on-accent', onAccentText(base));
      // Observed marker for the finance charts' MutationObserver (it watches
      // attributes, not inline style). Value = the resolved accent hex so a
      // change here reliably differs and trips a re-read.
      root.setAttribute('data-accent', shades.a);
    };

    apply();

    // Recompute shades when the theme flips (dark changes soft/ink/accent).
    const observer = new MutationObserver((records) => {
      if (records.some((r) => r.attributeName === 'data-theme')) apply();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [app, base]);
}
